<script setup>
import { computed } from 'vue';

// The big team tap target used by the live counter (rally scoring + setup choice):
// avatar (or initials), team name, and a caption saying exactly what the tap does.
const props = defineProps({
  name: { type: String, required: true },
  avatarUrl: { type: String, default: null },
  caption: { type: String, default: null },
});

const emit = defineEmits(['click']);

const initials = computed(() =>
    props.name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0].toUpperCase())
        .join('') || '?');
</script>

<template>
  <button class="ttb" type="button" @click="emit('click')">
    <span class="ttb-avatar">
      <img v-if="avatarUrl" :src="avatarUrl" alt="" />
      <span v-else class="ttb-initials">{{ initials }}</span>
    </span>
    <span class="ttb-name">{{ name }}</span>
    <span v-if="caption" class="ttb-caption">{{ caption }}</span>
  </button>
</template>

<style scoped>
.ttb {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-border));
  border-radius: 14px;
  padding: 16px 10px 14px;
  cursor: pointer;
  transition: transform 0.08s, background 0.1s, border-color 0.1s;
}
.ttb:hover {
  border-color: rgb(var(--v-theme-primary));
}
.ttb:active {
  transform: scale(0.97);
  background: rgb(var(--v-theme-brandTint));
}
.ttb:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.ttb-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--v-theme-brandTint));
}
.ttb-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.ttb-initials {
  font-size: 16px;
  font-weight: 800;
  color: rgb(var(--v-theme-primary));
}

.ttb-name {
  font-size: 14px;
  font-weight: 700;
  color: rgb(var(--v-theme-text));
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ttb-caption {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgb(var(--v-theme-textSubtle));
}
</style>
